<script setup lang="ts">
import { useAgentClient } from '@/data/Agents/useAgentClient'
import { useEntityStore } from '@/data/useEntityStore'
import CaseAgentFormMultiFileUpload from '@/modules/Cases/AgentForm/CaseAgentFormMultiFileUpload.vue'
import AgentLogo from '@/modules/Cases/Sidebar/AgentLogo.vue'
import { useNewCase } from '@/modules/Cases/useNewCase'
import { getEntityFieldComponent } from '@/modules/Project/Fields/field-registry'
import type { Field } from '@/modules/Project/Fields/types'
import type { Property } from '@/modules/Project/Properties/types'
import { useFieldDeleteFile } from '@/modules/Project/useFieldDeleteFile'
import { useFieldSaving } from '@/modules/Project/useFieldSaving'
import { useFieldUploadQueue } from '@/modules/Project/useFieldUploadQueue'
import { usePrevOrNextEntity } from '@/modules/Project/usePrevOrNextEntity'
import { useProjectContext } from '@/modules/Project/useProjectContext'
import { useProjectStore } from '@/modules/Project/useProjectStore'
import { useProjects } from '@/modules/Projects/useProjects'
import { toast } from '@/shared/toast'
import { invariant } from '@/shared/utils/typeAssertions'
import DividerLine from '@/uiKit/DividerLine.vue'
import IconButton from '@/uiKit/IconButton.vue'
import { computed, onMounted, watch } from 'vue'

const props = defineProps<{ agentId: string; entityId: string }>()

useFieldUploadQueue(2)

const projects = useProjects()
const entityStore = useEntityStore()
const currentProjectStore = useProjectStore()
const { saveFieldChange } = useFieldSaving()
const { loadAndSetEntity } = usePrevOrNextEntity()
const { deleteFile } = useFieldDeleteFile()

const {
  startNewCase,
  agentId: newCaseAgentId,
  statusText: newCaseStatusText,
} = useNewCase({ showsStatusToast: true })

onMounted(() => {
  newCaseAgentId.value = props.agentId
})

const entity = computed(() => entityStore.getFullOrPartialEntity(props.entityId))

const { projectId, workspaceId } = useProjectContext()

const project = computed(() => projects.getProjectById(projectId.value))

/** All subproject IDs from collections on this project */
const subprojectIds = computed(() => {
  const collectionProperties: Property<'collection'>[] = currentProjectStore.properties.filter(
    (property) => property.type === 'collection',
  )

  return collectionProperties.map((p) => p.config.subprojectConfig.child_project_id)
})

const agentData = useAgentClient({
  workspaceId,
}).fetchMultiple(subprojectIds)
const subprojectAgents = computed(() => agentData.value?.map((a) => a.data).filter((a) => !!a))

type FieldItem = {
  property: Property
  field: Field
  fileChildProperty: Property<'file'> | undefined
  dataChildProperty: Property<'data'> | undefined
}
const fields = computed<FieldItem[]>(() => {
  const fields: FieldItem[] = []
  if (!entity.value) return fields

  for (const property of currentProjectStore.visibleProperties) {
    if (property.tool !== 'manual') continue

    const field = entity.value.fields.get(property.id)
    invariant(field)
    if (property.type === 'collection') {
      /**
       * The special case for collection properties is when they have a single
       * file or data property. In this case, users will upload files to entities within
       * the collection.
       */
      const subprojectId = property.config.subprojectConfig.child_project_id
      const project = subprojectAgents.value.find((a) => a.id === subprojectId)

      const fileProperties = project?.properties.filter((p) => p.type === 'file')
      const dataProperties = project?.properties.filter((p) => p.type === 'data')

      if (fileProperties?.length === 1 && dataProperties?.length === 0) {
        // Single file property case
        const fileChildProperty = fileProperties[0]
        fields.push({ property, field, fileChildProperty, dataChildProperty: undefined })
      } else if (dataProperties?.length === 1 && fileProperties?.length === 0) {
        // Single data property case
        const dataChildProperty = dataProperties[0]
        fields.push({ property, field, fileChildProperty: undefined, dataChildProperty })
      }
    } else {
      fields.push({ property, field, fileChildProperty: undefined, dataChildProperty: undefined })
    }
  }

  return fields
})

/**
 * We need to load an entity first before editing,
 * our form field components all assume that they are editing an existing entity.
 */
watch(
  [() => props.entityId, () => currentProjectStore.projectLoaded],
  ([entityId, isLoaded]) => {
    if (!isLoaded) return
    loadAndSetEntity(entityId)
  },
  { immediate: true },
)

/**
 * Adopt the entity into a case, and redirect to the case.
 */
async function onSubmit() {
  if (newCaseStatusText.value !== undefined) return

  invariant(project.value?.id)
  newCaseAgentId.value = project.value.id

  const caseId = await startNewCase({
    entities: [{ projectId: project.value.id, entityId: props.entityId }],
    initialMessage: "Can you summarize the agent's results?",
    navigate: 'replace',
    workspaceId: workspaceId.value,
  })

  if (!caseId) {
    toast.error('Failed to start a case, please contact support if this issue persists.')
  }
}
</script>

<template>
  <div
    v-if="currentProjectStore && project"
    class="mx-auto max-w-[560px] overflow-auto py-16"
  >
    <div class="flex flex-col gap-8">
      <AgentLogo
        :project="project"
        class="size-14"
      >
        <div class="size-14" />
      </AgentLogo>

      <div class="flex flex-col gap-2 break-words">
        <h1 class="text-display-md-28px-default">
          {{ project.name }}
        </h1>
        <div class="text-lg-15px-light">
          {{ project.description }}
        </div>
      </div>

      <DividerLine color="subtle" />

      <div class="text-lg-15px-default">Fill in the inputs to run the agent</div>

      <!-- Form -->
      <div
        v-if="entity"
        class="flex flex-col gap-4"
      >
        <!-- Field form inputs -->
        <div
          v-for="item in fields"
          :key="item.property.id"
          class="flex flex-col gap-1 text-xs-11px-default"
        >
          <!-- Field label -->
          <div>{{ item.property.name }}</div>

          <!-- Field value form input -->
          <div class="w-full rounded-corner-8 bg-background-gray-subtlest">
            <CaseAgentFormMultiFileUpload
              v-if="
                item.property.type === 'collection' &&
                (item.fileChildProperty || item.dataChildProperty)
              "
              :property="item.property"
              :parent-entity-id="item.field.entityId"
              :file-property-id="item.fileChildProperty?.id || item.dataChildProperty?.id || ''"
              :child-property-type="item.fileChildProperty ? 'file' : 'data'"
            />
            <component
              :is="getEntityFieldComponent(item.field)"
              v-else
              :field="item.field"
              :property="item.property"
              :previewable="false"
              @submit="saveFieldChange(item.field, $event)"
              @file:delete="deleteFile($event, entity.parentEntityId)"
            />
          </div>
        </div>
      </div>

      <div class="flex justify-end">
        <IconButton
          icon="arrow-right"
          size="lg"
          class="w-12"
          variant="black"
          aria-label="Start agent"
          rounded
          @click="onSubmit"
        />
      </div>
    </div>
  </div>
</template>
